import React from 'react';
import { useSelector } from 'react-redux';
import { FaHardHat, FaRegClock, FaTools, FaTruckMoving, FaWrench } from 'react-icons/fa';
import 'bootstrap/dist/css/bootstrap.min.css';

const ServicesRent = () => {
  const { language } = useSelector((state) => state.language);

  const translations = {
    gb: {
      title: 'Why Rent With Us?',
      description:
        'Experience the difference with our professional equipment rental services backed by years of industry expertise. We provide reliable, well-maintained equipment with exceptional customer service.',
      buttonText: 'About Us',
      featureIcons: [
        {
          title: 'Latest & Well-Maintained Equipment',
          description: 'All our machinery undergoes regular maintenance and safety inspections',
        },
        {
          title: 'Expert Support',
          description: 'Our team provides expert advice and support for all your equipment needs',
        },
        {
          title: 'Fast Delivery',
          description: 'We ensure timely delivery of equipment to your job site',
        },
        {
          title: 'Comprehensive Service',
          description: 'Full-service support including setup, maintenance, and troubleshooting',
        },
      ],
    },
    sa: {
      title: 'لماذا تستأجر معنا؟',
      description:
        'استمتع بالفرق مع خدمات تأجير المعدات الاحترافية لدينا، المدعومة بسنوات من الخبرة الصناعية. نحن نقدم معدات موثوقة ومصانة جيدًا مع خدمة عملاء استثنائية.',
      buttonText: 'معلومات عنا',
      featureIcons: [
        {
          title: 'المعدات الحديثة والمصانة جيدًا',
          description: 'جميع معداتنا تخضع للصيانة الدورية وفحوصات السلامة',
        },
        {
          title: 'الدعم الخبير',
          description: 'فريقنا يقدم نصائح ودعمًا خبيرًا لجميع احتياجات المعدات الخاصة بك',
        },
        {
          title: 'التوصيل السريع',
          description: 'نحن نضمن تسليم المعدات في الوقت المحدد إلى موقع عملك',
        },
        {
          title: 'الخدمة الشاملة',
          description: 'دعم شامل يشمل الإعداد، الصيانة، واستكشاف الأخطاء وإصلاحها',
        },
      ],
    },
    fr: {
      title: 'Pourquoi louer avec nous ?',
      description:
        'Découvrez la différence avec nos services de location d’équipements professionnels, soutenus par des années d’expertise dans l’industrie. Nous fournissons des équipements fiables et bien entretenus avec un service client exceptionnel.',
      buttonText: 'À propos de nous',
      featureIcons: [
        {
          title: 'Équipements modernes et bien entretenus',
          description: 'Tous nos équipements sont soumis à un entretien régulier et à des inspections de sécurité',
        },
        {
          title: 'Support expert',
          description: 'Notre équipe fournit des conseils et un soutien experts pour tous vos besoins en équipements',
        },
        {
          title: 'Livraison rapide',
          description: 'Nous assurons une livraison ponctuelle des équipements sur votre site de travail',
        },
        {
          title: 'Service complet',
          description: 'Support complet incluant l’installation, l’entretien et le dépannage',
        },
      ],
    },
    de: {
      title: 'Warum bei uns mieten?',
      description:
        'Erleben Sie den Unterschied mit unseren professionellen Gerätemietdiensten, unterstützt durch jahrelange Branchenerfahrung. Wir bieten zuverlässige, gut gewartete Geräte mit außergewöhnlichem Kundenservice.',
      buttonText: 'Über uns',
      featureIcons: [
        {
          title: 'Moderne und gut gewartete Geräte',
          description: 'Alle unsere Maschinen werden regelmäßig gewartet und Sicherheitsprüfungen unterzogen',
        },
        {
          title: 'Expertenunterstützung',
          description: 'Unser Team bietet fachkundige Beratung und Unterstützung für alle Ihre Gerätebedürfnisse',
        },
        {
          title: 'Schnelle Lieferung',
          description: 'Wir gewährleisten eine pünktliche Lieferung der Geräte an Ihre Baustelle',
        },
        {
          title: 'Umfassender Service',
          description: 'Umfassender Support inklusive Einrichtung, Wartung und Fehlerbehebung',
        },
      ],
    },
  };

  const t = translations[language] || translations.gb;

  const iconStyle = {
    backgroundColor: '#B91C1C',
    color: 'white',
    padding: '10px',
    borderRadius: '50%',
  };

  return (
    <section
      className="why-rent-section py-5">
      <div className="container px-3">
        <div className="row">
          <div className="col-lg-4 mb-4 mb-lg-0">
            <h1 className="fw-bold display-4 display-md-3 display-lg-2 mb-4">
              {t.title}
              <div className="d-flex justify-content-start mt-2">
                <div
                  style={{
                    width: '40px',
                    height: '3px',
                    backgroundColor: '#c8102e',
                  }}
                ></div>
                <div
                  style={{
                    width: '5px',
                    height: '3px',
                    backgroundColor: '#c8102e',
                    margin: '0 4px',
                  }}
                ></div>
                <div
                  style={{
                    width: '40px',
                    height: '3px',
                    backgroundColor: '#c8102e',
                  }}
                ></div>
              </div>
            </h1>
            <p className="mb-4 text">{t.description}</p>
            <button
              className="btn btn-outline-dark rounded-pill d-flex align-items-center about btn-hover"
              style={{
                padding: '0.375rem 1rem',
                fontWeight: '500',
              }}
            >
              {t.buttonText}
              <span
                className="ms-2 d-inline-flex align-items-center justify-content-center btn-arrow"
                style={{
                  backgroundColor: '#B91C1C',
                  borderRadius: '50%',
                  width: '40px',
                  height: '40px',
                }}
              >
                <FaTools size={24} color="white" />
              </span>
            </button>
          </div>

          <div className="col-lg-8">
            <div className="row g-4">
              {t.featureIcons.map((feature, index) => (
                <div className="col-md-6" key={index}>
                  <div className="card-border h-100 p-4 card-hover">
                    <div className="d-flex align-items-center mb-3">
                      <div className="me-3">
                        {[
                          <FaRegClock size={40} style={iconStyle} />,
                          <FaHardHat size={40} style={iconStyle} />,
                          <FaTruckMoving size={40} style={iconStyle} />,
                          <FaWrench size={40} style={iconStyle} />,
                        ][index]}
                      </div>
                      <h5 className="m-0">{feature.title}</h5>
                    </div>
                    <p className="mb-0">{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Add hover styles */}
      <style>{`
        .card-hover:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
          transition: all 0.3s ease;
        }
        .btn-hover:hover .btn-arrow {
          transform: translateX(3px);
        }
        .btn-hover:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
          transition: all 0.3s ease;
        }
      `}</style>
    </section>
  );
};

export default ServicesRent;